import React, { useMemo } from 'react';

import { setViolation, type MatchScoreDraft } from './scoring-match-draft';
import type { MatchItem } from './scoring-types';

interface Props {
  match: MatchItem;
  draft: MatchScoreDraft;
  cancelLabel: string;
  onCancel: () => void;
}

export const ScoringMatchForm: React.FC<Props> = ({ match, draft, cancelLabel, onCancel }) => {
  const scoreOptions = useMemo(
    () => Array.from({ length: match.setEntry.maxScore + 1 }, (_, score) => score),
    [match.setEntry.maxScore],
  );

  return (
    <form onSubmit={draft.submit} className="scoring-match-form">
      <div className="scoring-set-row scoring-set-heading" aria-hidden="true">
        <span className="scoring-set-label" />
        <span className="scoring-set-side scoring-set-side--1" data-testid="set-heading-side-1">1</span>
        <span className="scoring-set-sep">:</span>
        <span className="scoring-set-side scoring-set-side--2" data-testid="set-heading-side-2">2</span>
      </div>
      {draft.visibleSets.map((set, index) => (
        <div key={index} className="scoring-set-block">
          <div className="scoring-set-row">
            <span className="scoring-set-label">Set {index + 1}</span>
            <select
              value={set.p1}
              onChange={(event) => draft.updateSet(index, 'p1', event.target.value)}
              className="scoring-set-input scoring-set-input--1"
              aria-label={`Set ${index + 1} — 1: ${match.player1Name}`}
              data-testid={`set-${index}-p1`}
            >
              <option value="">—</option>
              {scoreOptions.map((score) => (
                <option key={score} value={score}>{score}</option>
              ))}
            </select>
            <span className="scoring-set-sep">:</span>
            <select
              value={set.p2}
              onChange={(event) => draft.updateSet(index, 'p2', event.target.value)}
              className="scoring-set-input scoring-set-input--2"
              aria-label={`Set ${index + 1} — 2: ${match.player2Name}`}
              data-testid={`set-${index}-p2`}
            >
              <option value="">—</option>
              {scoreOptions.map((score) => (
                <option key={score} value={score}>{score}</option>
              ))}
            </select>
          </div>
          {setViolation(match.setEntry, set) && (
            <p className="scoring-set-violation" data-testid={`set-${index}-violation`}>
              {setViolation(match.setEntry, set)}
            </p>
          )}
        </div>
      ))}

      <label className="scoring-walkover">
        <span>Walkower</span>
        <select
          value={draft.walkover ?? ''}
          onChange={(event) => draft.setWalkover(event.target.value === '' ? null : (Number(event.target.value) as 1 | 2))}
          data-testid="walkover-select"
        >
          <option value="">nie — mecz rozegrany</option>
          <option value="1">wygrywa 1: {match.player1Name}</option>
          <option value="2">wygrywa 2: {match.player2Name}</option>
        </select>
      </label>

      {draft.error && (
        <p role="alert" data-testid="submit-error" className="scoring-submit-error">
          {draft.error}
        </p>
      )}

      <div className="scoring-form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={draft.submitting || draft.blocked}
          data-testid="submit-result"
        >
          {draft.submitting ? '…' : 'Zapisz wynik'}
        </button>
        {draft.canSavePartial && (
          <button
            type="button"
            className="btn btn-secondary scoring-save-partial"
            disabled={draft.submitting}
            onClick={draft.savePartial}
            data-testid="save-partial-result"
          >
            {draft.visibleSets.some((set) => set.p1 !== '' && set.p2 !== '')
              ? 'Zapisz częściowo'
              : 'Usuń zapis częściowy'}
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          {cancelLabel}
        </button>
      </div>
    </form>
  );
};

export default ScoringMatchForm;
